import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getDevisParToken, signerDevisWeb } from '../api';

const fmt = (s) => s ? new Date(s).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' }) : '—';
const fmtMoney = (n) => n != null ? `${Number(n).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €` : '—';

export default function VenteSignaturePage() {
  const { token } = useParams();
  const canvasRef  = useRef(null);
  const lastPos    = useRef(null);

  const [devis,        setDevis]        = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);
  const [drawing,      setDrawing]      = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [submitted,    setSubmitted]    = useState(false);
  const [submitting,   setSubmitting]   = useState(false);
  const [nomSignataire, setNomSignataire] = useState('');

  useEffect(() => {
    getDevisParToken(token)
      .then(r => {
        setDevis(r.data);
        setNomSignataire(r.data?.signature?.signataire_nom || r.data?.signataire_nom || '');
      })
      .catch(e => setError(e?.response?.data?.detail || 'Lien invalide ou expiré'))
      .finally(() => setLoading(false));
  }, [token]);

  /* ── Canvas helpers ─────────────────────────────────── */
  const getPos = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const src  = e.touches ? e.touches[0] : e;
    return {
      x: (src.clientX - rect.left) * (canvas.width  / rect.width),
      y: (src.clientY - rect.top)  * (canvas.height / rect.height),
    };
  };

  const startDraw = (e) => { e.preventDefault(); setDrawing(true); lastPos.current = getPos(e, canvasRef.current); };
  const draw = (e) => {
    e.preventDefault();
    if (!drawing) return;
    const canvas = canvasRef.current;
    const ctx    = canvas.getContext('2d');
    const pos    = getPos(e, canvas);
    ctx.beginPath();
    ctx.moveTo(lastPos.current.x, lastPos.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.strokeStyle = '#1A3A6E';
    ctx.lineWidth   = 2.5;
    ctx.lineCap     = 'round';
    ctx.stroke();
    lastPos.current = pos;
    setHasSignature(true);
  };
  const stopDraw  = () => setDrawing(false);
  const clearCanvas = () => {
    canvasRef.current.getContext('2d').clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    setHasSignature(false);
  };

  const handleSubmit = async () => {
    if (!nomSignataire.trim()) {
      alert('Merci de saisir votre nom complet avant de valider.');
      return;
    }
    if (!hasSignature) return;
    // Vérifier que le canvas n'est pas vide
    const blank = document.createElement('canvas');
    blank.width = canvasRef.current.width;
    blank.height = canvasRef.current.height;
    if (canvasRef.current.toDataURL() === blank.toDataURL()) {
      alert('Merci de signer dans le cadre avant de valider.');
      return;
    }
    setSubmitting(true);
    try {
      await signerDevisWeb(token, {
        signataire_nom:      nomSignataire.trim(),
        signature_image_b64: canvasRef.current.toDataURL('image/png'),
      });
      setSubmitted(true);
    } catch (e) {
      alert(e?.response?.data?.detail || 'Erreur lors de l\'envoi de la signature');
    } finally {
      setSubmitting(false);
    }
  };

  /* ── Lignes facturables ─────────────────────────────── */
  const lignes = devis?.lignes || [];

  /* ── États ──────────────────────────────────────────── */
  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F8FAFC' }}>
      <p style={{ color: '#64748B', fontSize: '0.9rem' }}>Chargement…</p>
    </div>
  );

  if (error) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#FEF2F2' }}>
      <div style={{ textAlign: 'center', padding: 32 }}>
        <p style={{ color: '#DC2626', fontSize: '1.1rem', fontWeight: 700 }}>Lien invalide</p>
        <p style={{ color: '#EF4444', fontSize: '0.875rem', marginTop: 8 }}>{error}</p>
      </div>
    </div>
  );

  if (devis?.deja_signe || submitted) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F0FDF4' }}>
      <div style={{ textAlign: 'center', padding: 32, maxWidth: 400 }}>
        <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#DCFCE7', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
          <svg width="32" height="32" fill="none" stroke="#15803D" strokeWidth="2.5" viewBox="0 0 24 24">
            <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
        <p style={{ color: '#15803D', fontSize: '1.25rem', fontWeight: 700 }}>Commande validée</p>
        <p style={{ color: '#166534', fontSize: '0.875rem', marginTop: 8 }}>
          {nomSignataire && <span>Merci <strong>{nomSignataire}</strong>. </span>}
          Votre signature a bien été enregistrée pour la commande <strong>{devis?.numero}</strong>.<br />
          Vous recevrez une confirmation par email.
        </p>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: '#F8FAFC',  }}>

      {/* Header */}
      <div style={{ background: '#1A3A6E', padding: '16px 24px' }}>
        <p style={{ color: '#fff', fontWeight: 800, fontSize: '1.1rem', margin: 0 }}>Bedee</p>
        <p style={{ color: '#93C5FD', fontSize: '0.75rem', margin: '2px 0 0' }}>Signature électronique — Bon de commande</p>
      </div>

      <div style={{ maxWidth: 640, margin: '24px auto', padding: '0 16px 40px' }}>

        {/* Récap commande */}
        <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #E2E8F0', padding: 20, marginBottom: 16 }}>
          <p style={{ color: '#94A3B8', fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 14px' }}>
            Bon de commande à signer
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
            <div>
              <p style={{ color: '#94A3B8', fontSize: '0.72rem', margin: '0 0 2px' }}>Numéro</p>
              <p style={{ color: '#1E293B', fontWeight: 700, fontSize: '1rem', margin: 0 }}>{devis.numero}</p>
            </div>
            <div>
              <p style={{ color: '#94A3B8', fontSize: '0.72rem', margin: '0 0 2px' }}>Client</p>
              <p style={{ color: '#1E293B', fontWeight: 600, fontSize: '0.875rem', margin: 0 }}>{devis.entreprise_nom || '—'}</p>
            </div>
            <div>
              <p style={{ color: '#94A3B8', fontSize: '0.72rem', margin: '0 0 2px' }}>Signataire</p>
              <p style={{ color: '#334155', fontSize: '0.875rem', margin: 0 }}>{devis?.signature?.signataire_nom || devis.signataire_nom || '—'}</p>
            </div>
            <div>
              <p style={{ color: '#94A3B8', fontSize: '0.72rem', margin: '0 0 2px' }}>Date</p>
              <p style={{ color: '#334155', fontSize: '0.875rem', margin: 0 }}>{fmt(devis.created_at)}</p>
            </div>
          </div>

          {/* Lignes */}
          {lignes.length > 0 && (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #E2E8F0' }}>
                  {['Désignation', 'Qté', 'PU HT', 'Total HT'].map(h => (
                    <th key={h} style={{ padding: '6px 8px', color: '#94A3B8', fontWeight: 600, textAlign: h === 'Désignation' ? 'left' : 'right', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {lignes.map((lg, i) => {
                  /* ── Section ── */
                  if (lg.type_ligne === 'section') {
                    return (
                      <tr key={i}>
                        <td colSpan={4} style={{ padding: '10px 8px 5px', color: '#1E293B', fontWeight: 700, fontSize: '0.82rem', letterSpacing: '0.02em', background: '#F8FAFC', borderBottom: '2px solid #E2E8F0' }}>
                          {lg.designation}
                        </td>
                      </tr>
                    );
                  }
                  /* ── Texte / commentaire ── */
                  if (lg.type_ligne === 'texte' || lg.type_ligne === 'commentaire') {
                    return (
                      <tr key={i}>
                        <td colSpan={4} style={{ padding: '5px 8px', color: '#64748B', fontStyle: 'italic', fontSize: '0.78rem', borderBottom: '1px solid #F1F5F9' }}>
                          {lg.designation}
                        </td>
                      </tr>
                    );
                  }
                  /* ── Article ── */
                  const total = (lg.quantite || 0) * (lg.prix_unitaire_ht || 0) * (1 - (lg.remise || 0) / 100);
                  return (
                    <tr key={i} style={{ borderBottom: '1px solid #F1F5F9' }}>
                      <td style={{ padding: '7px 8px', color: '#334155' }}>
                        {lg.reference && <span style={{ color: '#94A3B8', fontFamily: 'monospace', marginRight: 6, fontSize: '0.75rem' }}>{lg.reference}</span>}
                        {lg.designation}
                      </td>
                      <td style={{ padding: '7px 8px', textAlign: 'right', color: '#475569' }}>{lg.quantite}</td>
                      <td style={{ padding: '7px 8px', textAlign: 'right', color: '#475569' }}>{fmtMoney(lg.prix_unitaire_ht)}</td>
                      <td style={{ padding: '7px 8px', textAlign: 'right', color: '#1E293B', fontWeight: 600 }}>{fmtMoney(total)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {/* Totaux */}
          <div style={{ marginTop: 14, borderTop: '1px solid #E2E8F0', paddingTop: 12, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
            <div style={{ display: 'flex', gap: 32 }}>
              <span style={{ color: '#64748B', fontSize: '0.8rem' }}>Total HT</span>
              <span style={{ color: '#334155', fontWeight: 600, fontSize: '0.875rem', minWidth: 90, textAlign: 'right' }}>{fmtMoney(devis.total_ht)}</span>
            </div>
            <div style={{ display: 'flex', gap: 32 }}>
              <span style={{ color: '#64748B', fontSize: '0.8rem' }}>TVA</span>
              <span style={{ color: '#334155', fontSize: '0.875rem', minWidth: 90, textAlign: 'right' }}>{fmtMoney((devis.total_ttc || 0) - (devis.total_ht || 0))}</span>
            </div>
            <div style={{ display: 'flex', gap: 32, marginTop: 4 }}>
              <span style={{ color: '#1A3A6E', fontSize: '0.9rem', fontWeight: 700 }}>Total TTC</span>
              <span style={{ color: '#1A3A6E', fontWeight: 800, fontSize: '1rem', minWidth: 90, textAlign: 'right' }}>{fmtMoney(devis.total_ttc)}</span>
            </div>
          </div>
        </div>

        {/* Zone signature */}
        <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #E2E8F0', padding: 20 }}>
          <p style={{ color: '#94A3B8', fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 16px' }}>
            Votre signature
          </p>

          {/* Champ nom signataire */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', color: '#475569', fontSize: '0.78rem', fontWeight: 600, marginBottom: 6 }}>
              Nom complet <span style={{ color: '#EF4444' }}>*</span>
            </label>
            <input
              type="text"
              value={nomSignataire}
              onChange={e => setNomSignataire(e.target.value)}
              placeholder="Votre nom et prénom"
              data-testid="signataire-nom-input"
              style={{
                width: '100%', boxSizing: 'border-box',
                border: '1px solid #CBD5E1', borderRadius: 10,
                padding: '10px 14px', fontSize: '0.875rem', color: '#1E293B',
                outline: 'none', background: '#F8FAFC',
              }}
            />
          </div>

          <div style={{ border: '2px dashed #CBD5E1', borderRadius: 12, overflow: 'hidden', position: 'relative', touchAction: 'none' }}>
            <canvas
              ref={canvasRef}
              width={580} height={180}
              style={{ display: 'block', width: '100%', cursor: 'crosshair', background: '#FAFAFA' }}
              onMouseDown={startDraw} onMouseMove={draw} onMouseUp={stopDraw} onMouseLeave={stopDraw}
              onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={stopDraw}
            />
            {!hasSignature && (
              <p style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', color: '#CBD5E1', fontSize: '0.875rem', pointerEvents: 'none', margin: 0, whiteSpace: 'nowrap' }}>
                Signez ici
              </p>
            )}
          </div>

          <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
            <button onClick={clearCanvas}
              style={{ flex: '0 0 auto', padding: '12px 20px', borderRadius: 12, border: '1px solid #E2E8F0', background: '#F8FAFC', color: '#64748B', fontSize: '0.875rem', fontWeight: 600, cursor: 'pointer' }}>
              Effacer
            </button>
            <button onClick={handleSubmit} disabled={!hasSignature || submitting}
              style={{ flex: 1, padding: 12, borderRadius: 12, border: 'none',
                background: (hasSignature && nomSignataire.trim()) ? 'linear-gradient(135deg,#1A3A6E,#1D4ED8)' : '#E2E8F0',
                color: (hasSignature && nomSignataire.trim()) ? '#fff' : '#94A3B8', fontSize: '0.9rem', fontWeight: 700,
                cursor: (hasSignature && nomSignataire.trim()) ? 'pointer' : 'not-allowed' }}>
              {submitting ? 'Envoi…' : 'Valider ma commande'}
            </button>
          </div>

          <p style={{ color: '#94A3B8', fontSize: '0.72rem', textAlign: 'center', marginTop: 12 }}>
            En signant, vous confirmez votre commande {devis.numero} pour un montant de {fmtMoney(devis.total_ttc)} TTC.
          </p>
        </div>
      </div>
    </div>
  );
}
